import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, LockIcon, MailIcon, ShieldCheckIcon, HeadphonesIcon, ZapIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Simple validation
      if (email && password) {
        login({
          id: '1',
          name: 'Waqas',
          email: email,
          merchantId: '050102100023650',
          merchantName: 'Gadgets Zone'
        });
        navigate('/dashboard');
      } else {
        setError('Please enter valid credentials');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen flex bg-white" data-id="element-1411">
      {/* Left Panel - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#312E81] text-white p-12 flex-col justify-between" data-id="element-1412">
        {/* Animated Grid Overlay */}
        <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
        backgroundSize: '32px 32px'
      }} data-id="element-1413"></div>

        {/* Abstract Floating Shapes */}
        <div className="absolute top-1/4 right-10 w-64 h-40 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm transform rotate-12 shadow-2xl" data-id="element-1414"></div>
        <div className="absolute top-1/3 right-20 w-48 h-64 bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 rounded-2xl border border-white/10 backdrop-blur-md transform -rotate-6 shadow-2xl" data-id="element-1415"></div>

        <div className="relative z-10" data-id="element-1416">
          <h1 className="text-5xl font-bold mb-6 leading-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-indigo-200" data-id="element-1417">
            Welcome Back
          </h1>
          <p className="text-xl text-indigo-100/80 max-w-md font-light leading-relaxed" data-id="element-1418">
            Sign in to access your merchant dashboard and manage your payments
            securely.
          </p>
        </div>

        <div className="space-y-8 relative z-10 mb-12" data-id="element-1419">
          <div className="flex items-start space-x-5 group" data-id="element-1420">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-1421">
              <ShieldCheckIcon className="h-6 w-6 text-indigo-300" data-id="element-1422" />
            </div>
            <div data-id="element-1423">
              <h3 className="text-lg font-semibold mb-1 text-white" data-id="element-1424">
                Secure Access
              </h3>
              <p className="text-indigo-200/70 text-sm leading-relaxed" data-id="element-1425">
                Enterprise-grade security protection for your business data
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-5 group" data-id="element-1426">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-1427">
              <HeadphonesIcon className="h-6 w-6 text-blue-300" data-id="element-1428" />
            </div>
            <div data-id="element-1429">
              <h3 className="text-lg font-semibold mb-1 text-white" data-id="element-1430">
                24/7 Support
              </h3>
              <p className="text-indigo-200/70 text-sm leading-relaxed" data-id="element-1431">
                Our dedicated support team is always available to help
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-5 group" data-id="element-1432">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-1433">
              <ZapIcon className="h-6 w-6 text-emerald-300" data-id="element-1434" />
            </div>
            <div data-id="element-1435">
              <h3 className="text-lg font-semibold mb-1 text-white" data-id="element-1436">
                Fast Settlements
              </h3>
              <p className="text-indigo-200/70 text-sm leading-relaxed" data-id="element-1437">
                Track and receive your payments seamlessly
              </p>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-between text-indigo-200/50 text-sm" data-id="element-1438">
          <p data-id="element-1439">© {new Date().getFullYear()} Paysys. All rights reserved.</p>
          <div className="flex space-x-4" data-id="element-1440">
            <a href="#" className="hover:text-white transition-colors" data-id="element-1441">
              Privacy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-1442">
              Terms
            </a>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8FAFC]" data-id="element-1443">
        <div className="w-full max-w-md" data-id="element-1444">
          <div className="lg:hidden mb-10 text-center flex flex-col items-center" data-id="element-1445">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl flex items-center justify-center shadow-lg mb-4" data-id="element-1446">
              <div className="w-6 h-6 bg-white rounded-md" data-id="element-1447"></div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-1448">Paysys</h2>
          </div>

          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-10" data-id="element-1449">
            <div className="text-center mb-8" data-id="element-1450">
              <div className="flex justify-center mb-6" data-id="element-1451">
                <img src="https://cdn.magicpatterns.com/uploads/nd24yEEaW19maEQcbRUctL/Paysys_Logo.png" alt="Paysys Logo" className="h-12 object-contain" data-id="element-1452" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-1453">Sign In</h2>
              <p className="text-slate-500 mt-2 text-sm" data-id="element-1454">
                Enter your credentials to continue
              </p>
            </div>

            {error && <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm flex items-start" data-id="element-1455">
                <ShieldCheckIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-1456" />
                {error}
              </div>}

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1457">
              <div data-id="element-1458">
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1459">
                  Email Address
                </label>
                <div className="relative" data-id="element-1460">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1461">
                    <MailIcon className="h-5 w-5 text-slate-400" data-id="element-1462" />
                  </div>
                  <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full h-12 pl-11 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all" placeholder="you@example.com" required data-id="element-1463" />
                </div>
              </div>

              <div data-id="element-1464">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1465">
                  Password
                </label>
                <div className="relative" data-id="element-1466">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1467">
                    <LockIcon className="h-5 w-5 text-slate-400" data-id="element-1468" />
                  </div>
                  <input type={showPassword ? 'text' : 'password'} id="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full h-12 pl-11 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all" placeholder="Enter your password" required data-id="element-1469" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1470">
                    {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1471" /> : <EyeIcon className="h-5 w-5" data-id="element-1472" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2" data-id="element-1473">
                <label className="flex items-center cursor-pointer group" data-id="element-1474">
                  <div className="relative flex items-center justify-center" data-id="element-1475">
                    <input type="checkbox" className="peer sr-only" data-id="element-1476" />
                    <div className="w-5 h-5 border-2 border-slate-300 rounded peer-checked:bg-indigo-600 peer-checked:border-indigo-600 transition-all" data-id="element-1477"></div>
                    <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3" data-id="element-1478">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-1479" />
                    </svg>
                  </div>
                  <span className="ml-2 text-sm text-slate-600 group-hover:text-slate-900 transition-colors" data-id="element-1480">
                    Remember me
                  </span>
                </label>
                <a href="#" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors" data-id="element-1481">
                  Forgot password?
                </a>
              </div>

              <button type="submit" disabled={isLoading} className="w-full h-12 mt-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all shadow-md shadow-indigo-500/20 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center" data-id="element-1482">
                {isLoading ? <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1483">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1484"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1485"></path>
                  </svg> : 'Sign In'}
              </button>
            </form>

            <div className="mt-8 text-center" data-id="element-1486">
              <p className="text-slate-500 text-sm" data-id="element-1487">
                Don't have an account?{' '}
                <Link to="/onboarding" className="text-indigo-600 hover:text-indigo-700 font-semibold transition-colors" data-id="element-1488">
                  Register Now
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default LoginPage;